import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { jsPDF } from 'jspdf';
import { Project } from '../../types';
import { dossierAPI } from '../../services/api';
import { useIsMobile } from '../../hooks/useIsMobile';
import { Stakeholder, aanpakVoor, niveauDot } from '../omgevingsanalyse/StakeholdersEditor';
import { isHtml, htmlToPlain, htmlToMarkdown, htmlToBlocks, sanitizeHtml } from '../common/RichText';
import { BRAND, faseKleur } from '../../styles/brand';
import { normalizeParticipatie } from '../../constants/participatie';
import { deriveAvatar } from '../personas-fase/personaAvatars';

interface DossierModuleProps {
  project: Project;
}

// ---------- helpers ----------
const hasText = (v: any): boolean => typeof v === 'string' && (isHtml(v) ? htmlToPlain(v).trim().length > 0 : v.trim().length > 0);
// Voor de Markdown-export: rich-text → markdown (**vet**, *cursief*, - opsomming).
const markdownify = (v: any): any => {
  if (typeof v === 'string') return htmlToMarkdown(v);
  if (Array.isArray(v)) return v.map(markdownify);
  if (v && typeof v === 'object') { const o: any = {}; Object.keys(v).forEach((k) => { o[k] = markdownify(v[k]); }); return o; }
  return v;
};
const fmtDate = (d?: string | null): string =>
  d ? new Date(d).toLocaleDateString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric' }) : '';
const fmtDateTime = (d?: string | null): string =>
  d ? new Date(d).toLocaleString('nl-NL', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';
const num = (n: any): string => (typeof n === 'number' && !isNaN(n) ? n.toLocaleString('nl-NL') : '—');

const lockerName = (lb: any): string => {
  if (!lb || typeof lb !== 'object') return '';
  const naam = [lb.firstName, lb.lastName].filter(Boolean).join(' ').trim();
  return naam || lb.email || '';
};

const HOUDING: Record<string, { l: string; c: string }> = {
  zeer_negatief: { l: 'Zeer negatief', c: '#b91c1c' },
  negatief: { l: 'Negatief', c: '#d97706' },
  neutraal: { l: 'Neutraal', c: '#6b7280' },
  positief: { l: 'Positief', c: '#1f7a44' },
  zeer_positief: { l: 'Zeer positief', c: '#15803d' },
};
const houding = (h: string) => HOUDING[h] || HOUDING.neutraal;

const SENT: Record<string, string> = { positief: '#1f9d55', negatief: '#b91c1c', gemengd: '#d97706', neutraal: '#6b7280' };
const sentColor = (v?: string) => SENT[String(v || '').toLowerCase()] || '#6b7280';

const F1_FIELDS: [string, string][] = [
  ['doelstelling', 'Doelstelling van het project'],
  ['maatschappelijkeContext', 'Maatschappelijke & bestuurlijke context'],
  ['projectorganisatie', 'Projectorganisatie & besluitvorming'],
  ['belangrijksteStakeholders', 'Belangrijkste stakeholders'],
  ['kansenRisicos', "Kansen, risico's & gevoeligheden"],
  ['rolCommunicatie', 'Rol van communicatie bij de projectdoelen'],
];
const F2_FIELDS: [string, string][] = [
  ['gebiedskenmerken', 'Gebiedskenmerken'],
  ['sentimenten', 'Sentimenten'],
  ['belangenInformatiebehoeften', 'Belangen & informatiebehoeften'],
  ['kansen', 'Kansen'],
  ['risicosGevoeligheden', "Risico's & gevoeligheden"],
  ['impact', 'Impact'],
];
const F4_FIELDS: [string, string][] = [
  ['diagnoseKern', 'Kern van het communicatievraagstuk'],
  ['diagnoseOpgave', 'Strategische communicatieopgave'],
  ['diagnoseFunctie', 'Dominante communicatiefunctie'],
  ['diagnoseSpanning', 'Belangrijkste strategische spanning'],
  ['modelVerantwoording', 'Modelkeuze & verantwoording'],
  ['modelBeperkingen', 'Beperkingen & aandachtspunten'],
  // Bouwstenen Van Ruler — Strategisch Communicatie Frame (SCF)
  ['vrVisie', 'Visie'],
  ['vrInterneSituatie', 'Interne situatie'],
  ['vrExterneSituatie', 'Externe situatie'],
  ['vrAmbitie', 'Ambitie'],
  ['vrStakeholders', 'Stakeholders'],
  ['vrAanpak', 'Aanpak'],
  ['vrAccountability', 'Accountability'],
  ['vrResources', 'Resources'],
  // Bouwstenen Michels — Blok 1 · Analyse
  ['miOrganisatievraag', 'Organisatievraag'],
  ['miCommunicatievraag', 'Communicatievraag'],
  ['miSwot', 'Interne/externe analyse (SWOT)'],
  ['miDoelgroepenStakeholders', 'Doelgroepen & stakeholders'],
  // Bouwstenen Michels — Blok 2 · Strategie
  ['miPositionering', 'Positionering'],
  ['miPropositie', 'Propositie'],
  ['miDoelstellingen', 'Doelstellingen'],
  ['miKernboodschapStrategie', 'Kernboodschap / strategie'],
  // Bouwstenen Michels — Blok 3 · Aanpak
  ['miCreatiefConcept', 'Creatief concept'],
  ['miMiddelenMedia', 'Middelen & media (online · offline)'],
  ['miContent', 'Content'],
  ['miPlanningBudget', 'Planning & budget'],
  ['hyVerbindendeLijn', 'Verbindende strategische lijn (Van Ruler ↔ Michels)'],
  // Gedeeld
  ['narratief', 'Communicatienarratief'],
  ['participatieAanpak', 'Relatie met participatie'],
  ['communicatieafspraken', 'Communicatieafspraken'],
  ['risicosKansen', 'Communicatieve risico’s & kansen'],
  ['monitoringEvaluatie', 'Monitoring, evaluatie & bijsturing'],
];
const MODEL_LABEL: Record<string, string> = { 'van-ruler': 'Van Ruler — Strategisch Communicatie Frame', michels: 'Michels — communicatiemodel', hybride: 'Hybride' };
const PERSONA_FIELDS: [string, string][] = [
  ['rol', 'Rol in het project'],
  ['belangen', 'Belangen'],
  ['zorgen', 'Zorgen'],
  ['verwachtingen', 'Verwachtingen'],
  ['informatiebehoefte', 'Informatiebehoefte'],
  ['voorkennis', 'Voorkennis'],
  ['communicatievoorkeuren', 'Communicatievoorkeuren'],
  ['mediagebruik', 'Mediagebruik'],
  ['taalniveau', 'Taalniveau'],
];

const FASE_META = [
  { key: 'projectverkenning', nr: 1, titel: 'Projectverkenning', icon: <><path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" /><circle cx="12" cy="10" r="3" /></> },
  { key: 'omgevingsanalyse', nr: 2, titel: 'Omgevingsanalyse', icon: <><circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" /></> },
  { key: 'personas', nr: 3, titel: "Persona's", icon: <><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M23 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></> },
  { key: 'strategie', nr: 4, titel: 'Communicatiestrategie', icon: <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" /> },
  { key: 'kernboodschap', nr: 5, titel: 'Kernboodschap', icon: <><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="5" /><circle cx="12" cy="12" r="1" fill="currentColor" /></> },
  { key: 'communicatiewerkplaats', nr: 6, titel: 'Communicatiewerkplaats', icon: <path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z" /> },
];

/** Tekst van een middel in alinea's, zodat exports de witregels behouden. */
const alineas = (tekst: string): string[] => String(tekst || '').split(/\n\s*\n/).map((a) => a.trim()).filter(Boolean);
const middelMeta = (m: any): string => {
  const namen: string[] = Array.isArray(m?.personaNamen) ? m.personaNamen.filter(Boolean) : (m?.personaNaam ? [m.personaNaam] : []);
  return [m.typeLabel, namen.length ? `voor ${namen.join(', ')}` : '', m.kanaal].filter(Boolean).join(' · ');
};

const faseHasContent = (key: string, c: any): boolean => {
  if (!c) return false;
  if (key === 'projectverkenning') return F1_FIELDS.some(([k]) => hasText(c[k])) || hasText(c.communicatieopgave) || (c.__ai?.verhelderingsvragen?.length > 0);
  if (key === 'omgevingsanalyse') return F2_FIELDS.some(([k]) => hasText(c[k])) || (c.stakeholdersList?.length > 0) || !!c.gebiedskaartSnapshot || !!c.nieuwsSentiment;
  if (key === 'personas') return (c.personas?.length > 0);
  if (key === 'strategie') return F4_FIELDS.some(([k]) => hasText(c[k])) || (c.doelgroepen?.length > 0);
  if (key === 'kernboodschap') return hasText(c.kernboodschap) || (c.deelboodschappen?.filter((d: string) => hasText(d)).length > 0) || (c.pretest?.length > 0);
  if (key === 'communicatiewerkplaats') return (c.middelen?.length > 0);
  return false;
};

// ---------- styles ----------
const card: React.CSSProperties = { background: '#fff', border: '1px solid #ececf1', borderRadius: 14, padding: 24, marginBottom: 20 };
const fieldLabel: React.CSSProperties = { fontSize: 12.5, fontWeight: 700, color: '#8a90a0', textTransform: 'uppercase', letterSpacing: '0.03em', margin: '0 0 4px' };
const fieldVal: React.CSSProperties = { fontSize: 14.5, color: '#2b2f3a', lineHeight: 1.6, margin: '0 0 16px', whiteSpace: 'pre-wrap' };

const Field: React.FC<{ label: string; value?: string }> = ({ label, value }) => {
  if (!hasText(value)) return null;
  return (
    <div>
      <p style={fieldLabel}>{label}</p>
      {isHtml(value) ? (
        <div className="rt-content" style={{ ...fieldVal, whiteSpace: 'normal' }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(value as string) }} />
      ) : (
        <p style={fieldVal}>{value}</p>
      )}
    </div>
  );
};

const StatTile: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div style={{ background: '#f7f8fa', borderRadius: 10, padding: '12px 14px', flex: '1 1 120px', minWidth: 120 }}>
    <div style={{ fontSize: 20, fontWeight: 800, color: '#1a1a2e' }}>{value}</div>
    <div style={{ fontSize: 11.5, color: '#8a90a0', marginTop: 2 }}>{label}</div>
  </div>
);

/** Persona's uit het dossier: Factor C-ladder en herkomst (gebaseerdOp, met terugval op de oude 1-op-1-koppeling). */
function dossierPersonas(c: any): any[] {
  return (Array.isArray(c?.personas) ? c.personas : []).map((p: any) => ({
    ...p,
    participatieniveau: normalizeParticipatie(p?.participatieniveau),
    gebaseerdOp: String(p?.gebaseerdOp || p?.stakeholderNaam || '').split(';').map((x: string) => x.trim()).filter(Boolean).join(', '),
    // Foto opnieuw bepalen i.p.v. de opgeslagen (mogelijk verkeerde) foto te tonen.
    afbeelding: deriveAvatar(p || {}),
  }));
}
/** Doelgroepen uit Fase 4, met participatieniveau op de Factor C-ladder. */
function dossierDoelgroepen(c: any): any[] {
  return (Array.isArray(c?.doelgroepen) ? c.doelgroepen : []).map((d: any) => ({ ...d, participatieniveau: normalizeParticipatie(d?.participatieniveau) }));
}

// ---------- markdown export ----------

function buildMarkdown(project: Project, sections: any, klantNaam: string, plaats: string): string {
  const L: string[] = [];
  L.push(`# Strategisch Fundament — ${project.name}`);
  const meta = [klantNaam && `Klant: ${klantNaam}`, plaats && `Locatie: ${plaats}`].filter(Boolean).join(' · ');
  if (meta) L.push(`\n${meta}`);
  L.push(`\nGeëxporteerd op ${fmtDate(new Date().toISOString())}\n`);

  const st = (key: string, s: any) => {
    if (s?.status === 'locked') return `Gevalideerd${s.lockedAt ? ` op ${fmtDate(s.lockedAt)}` : ''}${lockerName(s.lockedBy) ? ` door ${lockerName(s.lockedBy)}` : ''}`;
    return faseHasContent(key, s?.content) ? 'In bewerking' : 'Nog niet ingevuld';
  };

  FASE_META.forEach((f) => {
    const s = sections[f.key] || {};
    const c = markdownify(s.content || {});
    L.push(`\n---\n\n## ${f.nr}. ${f.titel}`);
    L.push(`_Status: ${st(f.key, s)}_\n`);

    if (f.key === 'projectverkenning') {
      F1_FIELDS.forEach(([k, lab]) => { if (hasText(c[k])) L.push(`**${lab}**\n\n${c[k]}\n`); });
      if (hasText(c.communicatieopgave)) L.push(`**De communicatieopgave**\n\n${c.communicatieopgave}\n`);
      if (c.__ai?.verhelderingsvragen?.length) { L.push(`**Verhelderingsvragen**\n`); c.__ai.verhelderingsvragen.forEach((v: string) => L.push(`- ${v}`)); }
    } else if (f.key === 'omgevingsanalyse') {
      const snap = c.gebiedskaartSnapshot;
      if (snap) {
        L.push(`**Gebiedsanalyse (gebiedskaart)**\n`);
        if (snap.demografie) L.push(`- Inwoners: ${num(snap.demografie.inwoners)} · 65-plus: ${snap.demografie.percentage65Plus ?? '—'}% · Gem. inkomen: ${snap.demografie.gemiddeldInkomen ? '€' + num(snap.demografie.gemiddeldInkomen) : '—'}`);
        if (hasText(snap.gebiedsprofielSamenvatting)) L.push(`\n${snap.gebiedsprofielSamenvatting}\n`);
      }
      F2_FIELDS.forEach(([k, lab]) => { if (hasText(c[k])) L.push(`**${lab}**\n\n${c[k]}\n`); });
      const shl: Stakeholder[] = Array.isArray(c.stakeholdersList) ? c.stakeholdersList : [];
      if (shl.length) {
        L.push(`**Stakeholders (${shl.length})**\n`);
        L.push(`| Stakeholder | Groep | Invloed | Belang | Houding | Aanpak |`);
        L.push(`| --- | --- | --- | --- | --- | --- |`);
        shl.forEach((s) => L.push(`| ${s.naam || '—'} | ${s.groep || '—'} | ${niveauDot(s.invloed).label} | ${niveauDot(s.belang).label} | ${houding(s.houding).l} | ${aanpakVoor(s.invloed, s.belang).label} |`));
        L.push('');
      }
      const nw = c.nieuwsSentiment;
      if (nw) { L.push(`**Nieuws & sentiment** — overall: ${nw.overall} (score ${nw.score})`); if (hasText(nw.samenvatting)) L.push(`\n${nw.samenvatting}\n`); }
    } else if (f.key === 'personas') {
      const ps: any[] = dossierPersonas(c);
      if (!ps.length) L.push("_Nog geen persona's._");
      ps.forEach((p) => {
        L.push(`### ${p.naam || 'Naamloze persona'}${p.groep ? ` — ${p.groep}` : ''}`);
        const bio = [p.leeftijd && `${p.leeftijd} jaar`, p.geslacht, p.participatieniveau && `Participatie: ${p.participatieniveau}`].filter(Boolean).join(' · ');
        if (bio) L.push(`_${bio}_\n`);
        if (p.gebaseerdOp) L.push(`Gebaseerd op stakeholders: **${p.gebaseerdOp}**\n`);
        PERSONA_FIELDS.forEach(([k, lab]) => { if (hasText(p[k])) L.push(`- **${lab}:** ${p[k]}`); });
        if (hasText(p.huidigGedrag) || hasText(p.gewenstGedrag)) L.push(`- **Gedrag:** ${p.huidigGedrag || '—'} → ${p.gewenstGedrag || '—'}`);
        L.push('');
      });
    } else if (f.key === 'strategie') {
      if (c.modelKeuze) L.push(`**Gekozen communicatiemodel:** ${MODEL_LABEL[c.modelKeuze] || c.modelKeuze}\n`);
      F4_FIELDS.forEach(([k, lab]) => { if (hasText(c[k])) L.push(`**${lab}**\n\n${c[k]}\n`); });
      const sp: any[] = Array.isArray(c.communicatiesporen) ? c.communicatiesporen : [];
      if (sp.length) {
        L.push(`**Communicatiesporen**\n`);
        sp.forEach((s, i) => {
          L.push(`### ${s.naam || `Spoor ${i + 1}`}`);
          if (hasText(s.doel)) L.push(`- **Doel:** ${s.doel}`);
          if (hasText(s.betrokkenen)) L.push(`- **Betrokkenen:** ${s.betrokkenen}`);
          if (hasText(s.gewensteBeweging)) L.push(`- **Gewenste beweging:** ${s.gewensteBeweging}`);
          if (hasText(s.kernAanpak)) L.push(`- **Kern aanpak:** ${s.kernAanpak}`);
          if (hasText(s.risicos)) L.push(`- **Risico's:** ${s.risicos}`);
          L.push('');
        });
      }
      const fs: any[] = Array.isArray(c.fasering) ? c.fasering : [];
      if (fs.length) {
        L.push(`**Fasering & mijlpalen**\n`);
        fs.forEach((s, i) => {
          L.push(`### ${s.fase || `Fase ${i + 1}`}`);
          if (hasText(s.opgave)) L.push(`- **Opgave:** ${s.opgave}`);
          if (hasText(s.mijlpalen)) L.push(`- **Momenten/mijlpalen:** ${s.mijlpalen}`);
          if (hasText(s.aanpak)) L.push(`- **Aanpak:** ${s.aanpak}`);
          if (hasText(s.risicos)) L.push(`- **Risico's:** ${s.risicos}`);
          L.push('');
        });
      }
      const dg: any[] = dossierDoelgroepen(c);
      if (dg.length) {
        L.push(`**Strategie per doelgroep**\n`);
        dg.forEach((d) => {
          L.push(`### ${d.persona || 'Doelgroep'}`);
          if (hasText(d.doel)) L.push(`- **Doel:** ${d.doel}`);
          if (hasText(d.benadering)) L.push(`- **Benadering:** ${d.benadering}`);
          if (hasText(d.participatieniveau)) L.push(`- **Participatieniveau:** ${d.participatieniveau}`);
          if (hasText(d.middelen)) L.push(`- **Middelen:** ${d.middelen}`);
          L.push('');
        });
      }
    } else if (f.key === 'kernboodschap') {
      if (hasText(c.kernboodschap)) L.push(`**Centrale kernboodschap**\n\n> ${c.kernboodschap}\n`);
      const db: string[] = Array.isArray(c.deelboodschappen) ? c.deelboodschappen.filter(hasText) : [];
      if (db.length) { L.push(`**Deelboodschappen**\n`); db.forEach((d) => L.push(`- ${d}`)); L.push(''); }
      if (c.synthese) {
        L.push(`**Pretest — totaaladvies**\n`);
        if (hasText(c.synthese.algemeneReactie)) L.push(`${c.synthese.algemeneReactie}\n`);
        if (hasText(c.synthese.sterk)) L.push(`- **Sterk:** ${c.synthese.sterk}`);
        if (hasText(c.synthese.aandachtspunten)) L.push(`- **Aandachtspunten:** ${c.synthese.aandachtspunten}`);
        if (hasText(c.synthese.verbeteradvies)) L.push(`\n**Verbeteradvies:** ${c.synthese.verbeteradvies}`);
      }
    } else if (f.key === 'communicatiewerkplaats') {
      (Array.isArray(c.middelen) ? c.middelen : []).forEach((m: any) => {
        L.push(`### ${m.titel || m.typeLabel || 'Middel'}\n`);
        if (middelMeta(m)) L.push(`*${middelMeta(m)}*\n`);
        alineas(m.tekst).forEach((a) => L.push(`${a}\n`));
        if (Array.isArray(m.openpunten) && m.openpunten.length) {
          L.push(`**Nog invullen of controleren**\n`);
          m.openpunten.forEach((o: string) => L.push(`- ${o}`));
          L.push('');
        }
      });
    }
  });
  return L.join('\n');
}

const faseStatusText = (key: string, s: any): string => {
  if (s?.status === 'locked') return `Gevalideerd${s.lockedAt ? ' op ' + fmtDate(s.lockedAt) : ''}${lockerName(s.lockedBy) ? ' door ' + lockerName(s.lockedBy) : ''}`;
  return faseHasContent(key, s?.content) ? 'In bewerking' : 'Nog niet ingevuld';
};

const safeName = (project: Project): string => project.name.replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '');

// ---------- PDF-export (jsPDF, tekst — crisp & selecteerbaar) ----------
const hexRgb = (hex: string): number[] => [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16));
/** Huisstijl Leene: koppen in donkergroen. */
const GROEN = hexRgb(BRAND.groen);

/** Afbeelding (logo) als data-URL voor de PDF; null als hij niet te laden is (export gaat dan zonder logo). */
async function laadAfbeelding(url: string): Promise<string | null> {
  try {
    const r = await fetch(url);
    if (!r.ok) return null;
    const blob = await r.blob();
    return await new Promise((ok) => {
      const fr = new FileReader();
      fr.onload = () => ok(String(fr.result));
      fr.onerror = () => ok(null);
      fr.readAsDataURL(blob);
    });
  } catch (e) {
    return null;
  }
}

async function exportPdf(project: Project, sections: any, klantNaam: string, plaats: string) {
  const logo = await laadAfbeelding(`${process.env.PUBLIC_URL}/assets/logos/logo_leene.png`);
  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const M = 48;
  const maxW = pageW - M * 2;
  let y = M;

  const ensure = (h: number) => { if (y + h > pageH - M) { doc.addPage(); y = M; } };
  const write = (str: string, o: { size?: number; style?: string; color?: number[]; lh?: number; indent?: number; gap?: number } = {}) => {
    if (str == null || str === '') return;
    const { size = 10.5, style = 'normal', color = [43, 47, 58], lh = 1.35, indent = 0, gap = 6 } = o;
    doc.setFont('helvetica', style);
    doc.setFontSize(size);
    doc.setTextColor(color[0], color[1], color[2]);
    doc.splitTextToSize(String(str), maxW - indent).forEach((ln: string) => { ensure(size * lh); doc.text(ln, M + indent, y); y += size * lh; });
    y += gap;
  };
  const label = (s: string) => write(s.toUpperCase(), { size: 8.5, style: 'bold', color: [138, 144, 160], gap: 2 });
  const para = (s: string) => write(s, { size: 10.5, gap: 8 });
  const bullet = (s: string) => write('•  ' + s, { size: 10.5, indent: 6, gap: 3 });

  // Opgemaakte tekst (rich-text) met vet/cursief per run, regelwrap en opsommingstekens.
  const writeRuns = (runs: any[], o: any = {}) => {
    const { size = 10.5, indent = 0, gap = 4, bullet: isBullet = false, color = [43, 47, 58] } = o;
    doc.setFontSize(size);
    const left = M + indent + (isBullet ? 12 : 0);
    const maxLineW = maxW - indent - (isBullet ? 12 : 0);
    const fontFor = (b: boolean, i: boolean) => (b && i ? 'bolditalic' : b ? 'bold' : i ? 'italic' : 'normal');
    const tokens: any[] = [];
    runs.forEach((r) => {
      if (r.nl) { tokens.push({ nl: true }); return; }
      String(r.text).split(/(\s+)/).forEach((p) => { if (p !== '') tokens.push({ t: p, bold: !!r.bold, italic: !!r.italic }); });
    });
    let line: any[] = []; let lineW = 0; let firstLine = true;
    const flush = () => {
      ensure(size * 1.35);
      let x = left;
      if (isBullet && firstLine) { doc.setFont('helvetica', 'normal'); doc.setTextColor(color[0], color[1], color[2]); doc.text('•', M + indent, y); }
      line.forEach((tok) => { doc.setFont('helvetica', fontFor(tok.bold, tok.italic)); doc.setTextColor(color[0], color[1], color[2]); doc.text(tok.t, x, y); x += doc.getTextWidth(tok.t); });
      y += size * 1.35; line = []; lineW = 0; firstLine = false;
    };
    tokens.forEach((tok) => {
      if (tok.nl) { flush(); return; }
      const isSpace = /^\s+$/.test(tok.t);
      if (line.length === 0 && isSpace) return;
      doc.setFont('helvetica', fontFor(tok.bold, tok.italic));
      const w = doc.getTextWidth(tok.t);
      if (lineW + w > maxLineW && line.length && !isSpace) flush();
      if (line.length === 0 && isSpace) return;
      line.push(tok); lineW += w;
    });
    if (line.length) flush();
    y += gap;
  };
  const paraRich = (v: any) => {
    if (!isHtml(v)) { para(v); return; }
    try {
      htmlToBlocks(v).forEach((blk) => writeRuns(blk.runs, { bullet: !!blk.bullet, indent: blk.bullet ? 6 : 0, gap: blk.bullet ? 3 : 6 }));
      y += 2;
    } catch (e) { para(htmlToPlain(v)); }
  };

  doc.setFillColor(30, 58, 47); doc.rect(0, 0, pageW, 6, 'F');
  y = M + 6;
  if (logo) { doc.addImage(logo, 'PNG', M, y - 6, 52, 20); y += 40; }
  write('STRATEGISCH FUNDAMENT', { size: 10, style: 'bold', color: [30, 58, 47], gap: 4 });
  write(project.name, { size: 22, style: 'bold', color: GROEN, lh: 1.2, gap: 6 });
  const meta = [klantNaam, plaats].filter(Boolean).join('  ·  ');
  if (meta) write(meta, { size: 11, color: [107, 114, 128], gap: 4 });
  const gevalideerd = FASE_META.filter((f) => sections?.[f.key]?.status === 'locked').length;
  write(`${gevalideerd}/${FASE_META.length} fasen gevalideerd  ·  Geëxporteerd op ${fmtDate(new Date().toISOString())}`, { size: 9.5, color: [154, 161, 176], gap: 12 });

  FASE_META.forEach((f) => {
    const s = sections?.[f.key] || {};
    const c = s.content || {};
    ensure(46);
    const [fr, fg, fb] = hexRgb(faseKleur(f.nr).base);
    doc.setDrawColor(fr, fg, fb); doc.setLineWidth(3); doc.line(M, y, pageW - M, y); y += 17;
    write(`${f.nr}. ${f.titel}`, { size: 15, style: 'bold', color: GROEN, gap: 3 });
    write(faseStatusText(f.key, s), { size: 9, style: 'italic', color: s.status === 'locked' ? [30, 58, 47] : [154, 161, 176], gap: 10 });

    if (!faseHasContent(f.key, c)) { write('Nog niet ingevuld.', { size: 10.5, color: [154, 161, 176], gap: 8 }); return; }

    if (f.key === 'projectverkenning') {
      if (hasText(c.communicatieopgave)) { label('De communicatieopgave'); paraRich(c.communicatieopgave); }
      F1_FIELDS.forEach(([k, lab]) => { if (hasText(c[k])) { label(lab); paraRich(c[k]); } });
      if (c.__ai?.verhelderingsvragen?.length) { label('Verhelderingsvragen'); c.__ai.verhelderingsvragen.forEach((v: string) => bullet(v)); y += 4; }
    } else if (f.key === 'omgevingsanalyse') {
      const snap = c.gebiedskaartSnapshot;
      if (snap) {
        label('Gebiedsanalyse');
        const d = snap.demografie || {};
        para(`Inwoners: ${num(d.inwoners)}   ·   65-plus: ${d.percentage65Plus ?? '—'}%   ·   Gem. inkomen: ${d.gemiddeldInkomen ? '€' + num(d.gemiddeldInkomen) : '—'}   ·   Oppervlakte: ${snap.areaSqmHa != null ? num(snap.areaSqmHa) + ' ha' : '—'}`);
        if (hasText(snap.gebiedsprofielSamenvatting)) para(snap.gebiedsprofielSamenvatting);
      }
      F2_FIELDS.forEach(([k, lab]) => { if (hasText(c[k])) { label(lab); paraRich(c[k]); } });
      const shl: Stakeholder[] = Array.isArray(c.stakeholdersList) ? c.stakeholdersList : [];
      if (shl.length) {
        label(`Stakeholders (${shl.length})`);
        shl.forEach((st) => write(`${st.naam || '—'}${st.groep ? ' (' + st.groep + ')' : ''} — invloed ${niveauDot(st.invloed).label.toLowerCase()}, belang ${niveauDot(st.belang).label.toLowerCase()}, houding ${houding(st.houding).l.toLowerCase()} → ${aanpakVoor(st.invloed, st.belang).label}`, { size: 10, indent: 6, gap: 3 }));
        y += 4;
      }
      const nw = c.nieuwsSentiment;
      if (nw) { label('Nieuws & sentiment'); para(`Overall: ${nw.overall} (score ${nw.score}, ${Array.isArray(nw.items) ? nw.items.length : 0} berichten). ${nw.samenvatting || ''}`); }
    } else if (f.key === 'personas') {
      const ps: any[] = dossierPersonas(c);
      ps.forEach((p) => {
        write(`${p.naam || 'Naamloze persona'}${p.groep ? ' — ' + p.groep : ''}`, { size: 12, style: 'bold', color: GROEN, gap: 2 });
        const bio = [p.leeftijd && `${p.leeftijd} jaar`, p.geslacht, p.participatieniveau && `Participatie: ${p.participatieniveau}`].filter(Boolean).join('  ·  ');
        if (bio) write(bio, { size: 9, style: 'italic', color: [138, 144, 160], gap: 4 });
        if (p.gebaseerdOp) write(`Gebaseerd op stakeholders: ${p.gebaseerdOp}`, { size: 9.5, color: [107, 114, 128], gap: 4 });
        PERSONA_FIELDS.forEach(([k, lab]) => { if (hasText(p[k])) write(`${lab}: ${p[k]}`, { size: 10, indent: 6, gap: 3 }); });
        if (hasText(p.huidigGedrag) || hasText(p.gewenstGedrag)) write(`Gedrag: ${p.huidigGedrag || '—'} → ${p.gewenstGedrag || '—'}`, { size: 10, indent: 6, gap: 3 });
        y += 6;
      });
    } else if (f.key === 'strategie') {
      if (c.modelKeuze) { label('Gekozen communicatiemodel'); para(MODEL_LABEL[c.modelKeuze] || c.modelKeuze); }
      F4_FIELDS.forEach(([k, lab]) => { if (hasText(c[k])) { label(lab); paraRich(c[k]); } });
      const sp: any[] = Array.isArray(c.communicatiesporen) ? c.communicatiesporen : [];
      if (sp.length) {
        label('Communicatiesporen');
        sp.forEach((s, i) => {
          write(s.naam || `Spoor ${i + 1}`, { size: 11, style: 'bold', color: GROEN, gap: 2 });
          if (hasText(s.doel)) write(`Doel: ${s.doel}`, { size: 10, indent: 6, gap: 2 });
          if (hasText(s.betrokkenen)) write(`Betrokkenen: ${s.betrokkenen}`, { size: 10, indent: 6, gap: 2 });
          if (hasText(s.gewensteBeweging)) write(`Gewenste beweging: ${s.gewensteBeweging}`, { size: 10, indent: 6, gap: 2 });
          if (hasText(s.kernAanpak)) write(`Kern aanpak: ${s.kernAanpak}`, { size: 10, indent: 6, gap: 2 });
          if (hasText(s.risicos)) write(`Risico's: ${s.risicos}`, { size: 10, indent: 6, gap: 2 });
          y += 6;
        });
      }
      const fs: any[] = Array.isArray(c.fasering) ? c.fasering : [];
      if (fs.length) {
        label('Fasering & mijlpalen');
        fs.forEach((s, i) => {
          write(s.fase || `Fase ${i + 1}`, { size: 11, style: 'bold', color: GROEN, gap: 2 });
          if (hasText(s.opgave)) write(`Opgave: ${s.opgave}`, { size: 10, indent: 6, gap: 2 });
          if (hasText(s.mijlpalen)) write(`Momenten/mijlpalen: ${s.mijlpalen}`, { size: 10, indent: 6, gap: 2 });
          if (hasText(s.aanpak)) write(`Aanpak: ${s.aanpak}`, { size: 10, indent: 6, gap: 2 });
          if (hasText(s.risicos)) write(`Risico's: ${s.risicos}`, { size: 10, indent: 6, gap: 2 });
          y += 6;
        });
      }
      const dg: any[] = dossierDoelgroepen(c);
      if (dg.length) {
        label('Strategie per doelgroep');
        dg.forEach((d) => {
          write(d.persona || 'Doelgroep', { size: 11, style: 'bold', color: GROEN, gap: 2 });
          if (hasText(d.doel)) write(`Doel: ${d.doel}`, { size: 10, indent: 6, gap: 2 });
          if (hasText(d.benadering)) write(`Benadering: ${d.benadering}`, { size: 10, indent: 6, gap: 2 });
          if (hasText(d.participatieniveau)) write(`Participatieniveau: ${d.participatieniveau}`, { size: 10, indent: 6, gap: 2 });
          if (hasText(d.middelen)) write(`Middelen: ${d.middelen}`, { size: 10, indent: 6, gap: 2 });
          y += 6;
        });
      }
    } else if (f.key === 'kernboodschap') {
      if (hasText(c.kernboodschap)) { label('Centrale kernboodschap'); paraRich(c.kernboodschap); }
      const db: string[] = Array.isArray(c.deelboodschappen) ? c.deelboodschappen.filter(hasText) : [];
      if (db.length) { label('Deelboodschappen'); db.forEach((d) => bullet(d)); y += 4; }
      if (c.synthese) {
        label('Pretest — totaaladvies');
        if (hasText(c.synthese.algemeneReactie)) para(c.synthese.algemeneReactie);
        if (hasText(c.synthese.sterk)) write(`Sterk: ${c.synthese.sterk}`, { size: 10, indent: 6, gap: 3 });
        if (hasText(c.synthese.aandachtspunten)) write(`Aandachtspunten: ${c.synthese.aandachtspunten}`, { size: 10, indent: 6, gap: 3 });
        if (hasText(c.synthese.verbeteradvies)) para(`Verbeteradvies: ${c.synthese.verbeteradvies}`);
      }
    } else if (f.key === 'communicatiewerkplaats') {
      (Array.isArray(c.middelen) ? c.middelen : []).forEach((m: any) => {
        label(m.titel || m.typeLabel || 'Middel');
        if (middelMeta(m)) write(middelMeta(m), { size: 9.5, color: [138, 144, 160], gap: 4 });
        alineas(m.tekst).forEach((a) => para(a));
        if (Array.isArray(m.openpunten) && m.openpunten.length) {
          write('Nog invullen of controleren', { size: 9.5, style: 'bold', color: [146, 64, 14], gap: 2 });
          m.openpunten.forEach((o: string) => bullet(o));
        }
        y += 6;
      });
    }
  });

  // Voet op elke pagina: afzender en paginanummer.
  const totaal = doc.getNumberOfPages();
  for (let n = 1; n <= totaal; n += 1) {
    doc.setPage(n);
    doc.setDrawColor(217, 226, 213); doc.setLineWidth(0.5); doc.line(M, pageH - 32, pageW - M, pageH - 32);
    doc.setFont('helvetica', 'normal'); doc.setFontSize(8); doc.setTextColor(138, 144, 160);
    doc.text(`${project.name}  ·  OmgevingsCompas  ·  Leene Communicatie`, M, pageH - 20);
    doc.text(`Pagina ${n} van ${totaal}`, pageW - M, pageH - 20, { align: 'right' });
  }

  doc.save(`Dossier-${safeName(project)}.pdf`);
}

// ---------- Word-export (HTML → .doc, opent in Word) ----------
function exportWord(project: Project, sections: any, klantNaam: string, plaats: string) {
  const esc = (s: any) => String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  // Word rendert HTML native: rich-text-velden opgemaakt injecteren, platte tekst escapen.
  const p = (s: string) => isHtml(s)
    ? `<div style="margin:0 0 8pt;font-size:11pt;color:#2b2f3a;line-height:1.5;">${sanitizeHtml(s)}</div>`
    : `<p style="margin:0 0 8pt;font-size:11pt;color:#2b2f3a;line-height:1.5;">${esc(s).replace(/\n/g, '<br/>')}</p>`;
  const lab = (s: string) => `<p style="margin:10pt 0 2pt;font-size:8.5pt;font-weight:bold;color:#8a90a0;">${esc(s).toUpperCase()}</p>`;
  const parts: string[] = [];

  parts.push(`<div style="border-top:3pt solid #1E3A2F;padding-top:8pt;margin-bottom:16pt;">`);
  parts.push(`<p style="margin:0 0 10pt;"><img src="${window.location.origin}${process.env.PUBLIC_URL}/assets/logos/logo_leene.png" width="104" height="40" alt="Leene Communicatie"/></p>`);
  parts.push(`<p style="font-size:10pt;font-weight:bold;color:#1E3A2F;margin:0;">STRATEGISCH FUNDAMENT</p>`);
  parts.push(`<h1 style="font-size:22pt;color:#1E3A2F;margin:4pt 0 6pt;">${esc(project.name)}</h1>`);
  const metaLine = [klantNaam, plaats].filter(Boolean).map(esc).join(' &middot; ');
  if (metaLine) parts.push(`<p style="font-size:11pt;color:#6b7280;margin:0 0 2pt;">${metaLine}</p>`);
  const gevalideerd = FASE_META.filter((f) => sections?.[f.key]?.status === 'locked').length;
  parts.push(`<p style="font-size:9.5pt;color:#9aa1b0;margin:0;">${gevalideerd}/${FASE_META.length} fasen gevalideerd &middot; Geëxporteerd op ${esc(fmtDate(new Date().toISOString()))}</p>`);
  parts.push(`</div>`);

  FASE_META.forEach((f) => {
    const s = sections?.[f.key] || {};
    const c = s.content || {};
    parts.push(`<hr style="border:none;border-top:1pt solid #ececf2;margin:16pt 0 8pt;"/>`);
    parts.push(`<h2 style="font-size:15pt;color:#1E3A2F;margin:0 0 2pt;border-left:6pt solid ${faseKleur(f.nr).base};padding-left:6pt;">${f.nr}. ${esc(f.titel)}</h2>`);
    parts.push(`<p style="font-size:9pt;font-style:italic;color:${s.status === 'locked' ? '#1E3A2F' : '#9aa1b0'};margin:0 0 10pt;">${esc(faseStatusText(f.key, s))}</p>`);

    if (!faseHasContent(f.key, c)) { parts.push(p('Nog niet ingevuld.')); return; }

    if (f.key === 'projectverkenning') {
      if (hasText(c.communicatieopgave)) { parts.push(lab('De communicatieopgave')); parts.push(isHtml(c.communicatieopgave) ? `<div style="margin:0 0 8pt;font-size:11pt;font-weight:bold;color:#1e3a2f;">${sanitizeHtml(c.communicatieopgave)}</div>` : `<p style="margin:0 0 8pt;font-size:11pt;font-weight:bold;color:#1e3a2f;">${esc(c.communicatieopgave)}</p>`); }
      F1_FIELDS.forEach(([k, l]) => { if (hasText(c[k])) { parts.push(lab(l)); parts.push(p(c[k])); } });
      if (c.__ai?.verhelderingsvragen?.length) { parts.push(lab('Verhelderingsvragen')); parts.push('<ul>' + c.__ai.verhelderingsvragen.map((v: string) => `<li style="font-size:11pt;color:#2b2f3a;">${esc(v)}</li>`).join('') + '</ul>'); }
    } else if (f.key === 'omgevingsanalyse') {
      const snap = c.gebiedskaartSnapshot;
      if (snap) {
        parts.push(lab('Gebiedsanalyse'));
        const d = snap.demografie || {};
        parts.push(p(`Inwoners: ${num(d.inwoners)} · 65-plus: ${d.percentage65Plus ?? '—'}% · Gem. inkomen: ${d.gemiddeldInkomen ? '€' + num(d.gemiddeldInkomen) : '—'} · Oppervlakte: ${snap.areaSqmHa != null ? num(snap.areaSqmHa) + ' ha' : '—'}`));
        if (hasText(snap.gebiedsprofielSamenvatting)) parts.push(p(snap.gebiedsprofielSamenvatting));
      }
      F2_FIELDS.forEach(([k, l]) => { if (hasText(c[k])) { parts.push(lab(l)); parts.push(p(c[k])); } });
      const shl: Stakeholder[] = Array.isArray(c.stakeholdersList) ? c.stakeholdersList : [];
      if (shl.length) {
        parts.push(lab(`Stakeholders (${shl.length})`));
        const rows = shl.map((st) => `<tr><td style="padding:3pt 6pt;border:0.5pt solid #e5e7eb;font-weight:bold;">${esc(st.naam || '—')}</td><td style="padding:3pt 6pt;border:0.5pt solid #e5e7eb;">${esc(st.groep || '—')}</td><td style="padding:3pt 6pt;border:0.5pt solid #e5e7eb;">${esc(niveauDot(st.invloed).label)}</td><td style="padding:3pt 6pt;border:0.5pt solid #e5e7eb;">${esc(niveauDot(st.belang).label)}</td><td style="padding:3pt 6pt;border:0.5pt solid #e5e7eb;">${esc(houding(st.houding).l)}</td><td style="padding:3pt 6pt;border:0.5pt solid #e5e7eb;">${esc(aanpakVoor(st.invloed, st.belang).label)}</td></tr>`).join('');
        parts.push(`<table style="border-collapse:collapse;font-size:10pt;width:100%;"><tr style="background:#f4f5f7;"><th style="padding:3pt 6pt;border:0.5pt solid #e5e7eb;text-align:left;">Stakeholder</th><th style="padding:3pt 6pt;border:0.5pt solid #e5e7eb;text-align:left;">Groep</th><th style="padding:3pt 6pt;border:0.5pt solid #e5e7eb;text-align:left;">Invloed</th><th style="padding:3pt 6pt;border:0.5pt solid #e5e7eb;text-align:left;">Belang</th><th style="padding:3pt 6pt;border:0.5pt solid #e5e7eb;text-align:left;">Houding</th><th style="padding:3pt 6pt;border:0.5pt solid #e5e7eb;text-align:left;">Aanpak</th></tr>${rows}</table>`);
      }
      const nw = c.nieuwsSentiment;
      if (nw) { parts.push(lab('Nieuws & sentiment')); parts.push(p(`Overall: ${nw.overall} (score ${nw.score}, ${Array.isArray(nw.items) ? nw.items.length : 0} berichten). ${nw.samenvatting || ''}`)); }
    } else if (f.key === 'personas') {
      const ps: any[] = dossierPersonas(c);
      ps.forEach((pp) => {
        parts.push(`<h3 style="font-size:12pt;color:#1E3A2F;margin:10pt 0 2pt;">${esc(pp.naam || 'Naamloze persona')}${pp.groep ? ' — ' + esc(pp.groep) : ''}</h3>`);
        const bio = [pp.leeftijd && `${pp.leeftijd} jaar`, pp.geslacht, pp.participatieniveau && `Participatie: ${pp.participatieniveau}`].filter(Boolean).map(esc).join(' · ');
        if (bio) parts.push(`<p style="font-size:9pt;font-style:italic;color:#8a90a0;margin:0 0 4pt;">${bio}</p>`);
        if (pp.gebaseerdOp) parts.push(`<p style="font-size:9.5pt;color:#6b7280;margin:0 0 4pt;">Gebaseerd op stakeholders: ${esc(pp.gebaseerdOp)}</p>`);
        PERSONA_FIELDS.forEach(([k, l]) => { if (hasText(pp[k])) parts.push(`<p style="font-size:10.5pt;color:#2b2f3a;margin:0 0 3pt;"><b>${esc(l)}:</b> ${esc(pp[k])}</p>`); });
        if (hasText(pp.huidigGedrag) || hasText(pp.gewenstGedrag)) parts.push(`<p style="font-size:10.5pt;color:#2b2f3a;margin:0 0 3pt;"><b>Gedrag:</b> ${esc(pp.huidigGedrag || '—')} → ${esc(pp.gewenstGedrag || '—')}</p>`);
      });
    } else if (f.key === 'strategie') {
      if (c.modelKeuze) { parts.push(lab('Gekozen communicatiemodel')); parts.push(p(MODEL_LABEL[c.modelKeuze] || c.modelKeuze)); }
      F4_FIELDS.forEach(([k, l]) => { if (hasText(c[k])) { parts.push(lab(l)); parts.push(p(c[k])); } });
      const sp: any[] = Array.isArray(c.communicatiesporen) ? c.communicatiesporen : [];
      if (sp.length) {
        parts.push(lab('Communicatiesporen'));
        sp.forEach((s, i) => {
          parts.push(`<p style="font-size:11pt;font-weight:bold;color:#1E3A2F;margin:8pt 0 2pt;">${esc(s.naam || `Spoor ${i + 1}`)}</p>`);
          if (hasText(s.doel)) parts.push(`<p style="font-size:10.5pt;color:#2b2f3a;margin:0 0 2pt;"><b>Doel:</b> ${esc(s.doel)}</p>`);
          if (hasText(s.betrokkenen)) parts.push(`<p style="font-size:10.5pt;color:#2b2f3a;margin:0 0 2pt;"><b>Betrokkenen:</b> ${esc(s.betrokkenen)}</p>`);
          if (hasText(s.gewensteBeweging)) parts.push(`<p style="font-size:10.5pt;color:#2b2f3a;margin:0 0 2pt;"><b>Gewenste beweging:</b> ${esc(s.gewensteBeweging)}</p>`);
          if (hasText(s.kernAanpak)) parts.push(`<p style="font-size:10.5pt;color:#2b2f3a;margin:0 0 2pt;"><b>Kern aanpak:</b> ${esc(s.kernAanpak)}</p>`);
          if (hasText(s.risicos)) parts.push(`<p style="font-size:10.5pt;color:#2b2f3a;margin:0 0 2pt;"><b>Risico's:</b> ${esc(s.risicos)}</p>`);
        });
      }
      const fs: any[] = Array.isArray(c.fasering) ? c.fasering : [];
      if (fs.length) {
        parts.push(lab('Fasering & mijlpalen'));
        fs.forEach((s, i) => {
          parts.push(`<p style="font-size:11pt;font-weight:bold;color:#1E3A2F;margin:8pt 0 2pt;">${esc(s.fase || `Fase ${i + 1}`)}</p>`);
          if (hasText(s.opgave)) parts.push(`<p style="font-size:10.5pt;color:#2b2f3a;margin:0 0 2pt;"><b>Opgave:</b> ${esc(s.opgave)}</p>`);
          if (hasText(s.mijlpalen)) parts.push(`<p style="font-size:10.5pt;color:#2b2f3a;margin:0 0 2pt;"><b>Momenten/mijlpalen:</b> ${esc(s.mijlpalen)}</p>`);
          if (hasText(s.aanpak)) parts.push(`<p style="font-size:10.5pt;color:#2b2f3a;margin:0 0 2pt;"><b>Aanpak:</b> ${esc(s.aanpak)}</p>`);
          if (hasText(s.risicos)) parts.push(`<p style="font-size:10.5pt;color:#2b2f3a;margin:0 0 2pt;"><b>Risico's:</b> ${esc(s.risicos)}</p>`);
        });
      }
      const dg: any[] = dossierDoelgroepen(c);
      if (dg.length) {
        parts.push(lab('Strategie per doelgroep'));
        dg.forEach((d) => {
          parts.push(`<p style="font-size:11pt;font-weight:bold;color:#1E3A2F;margin:8pt 0 2pt;">${esc(d.persona || 'Doelgroep')}</p>`);
          if (hasText(d.doel)) parts.push(`<p style="font-size:10.5pt;color:#2b2f3a;margin:0 0 2pt;"><b>Doel:</b> ${esc(d.doel)}</p>`);
          if (hasText(d.benadering)) parts.push(`<p style="font-size:10.5pt;color:#2b2f3a;margin:0 0 2pt;"><b>Benadering:</b> ${esc(d.benadering)}</p>`);
          if (hasText(d.participatieniveau)) parts.push(`<p style="font-size:10.5pt;color:#2b2f3a;margin:0 0 2pt;"><b>Participatieniveau:</b> ${esc(d.participatieniveau)}</p>`);
          if (hasText(d.middelen)) parts.push(`<p style="font-size:10.5pt;color:#2b2f3a;margin:0 0 2pt;"><b>Middelen:</b> ${esc(d.middelen)}</p>`);
        });
      }
    } else if (f.key === 'kernboodschap') {
      if (hasText(c.kernboodschap)) { parts.push(lab('Centrale kernboodschap')); parts.push(isHtml(c.kernboodschap) ? `<div style="font-size:13pt;font-weight:bold;color:#1e3a2f;margin:0 0 8pt;">${sanitizeHtml(c.kernboodschap)}</div>` : `<p style="font-size:13pt;font-weight:bold;color:#1e3a2f;margin:0 0 8pt;">${esc(c.kernboodschap)}</p>`); }
      const db: string[] = Array.isArray(c.deelboodschappen) ? c.deelboodschappen.filter(hasText) : [];
      if (db.length) { parts.push(lab('Deelboodschappen')); parts.push('<ul>' + db.map((d) => `<li style="font-size:11pt;color:#2b2f3a;">${esc(d)}</li>`).join('') + '</ul>'); }
      if (c.synthese) {
        parts.push(lab('Pretest — totaaladvies'));
        if (hasText(c.synthese.algemeneReactie)) parts.push(p(c.synthese.algemeneReactie));
        if (hasText(c.synthese.sterk)) parts.push(`<p style="font-size:10.5pt;color:#2b2f3a;margin:0 0 3pt;"><b>Sterk:</b> ${esc(c.synthese.sterk)}</p>`);
        if (hasText(c.synthese.aandachtspunten)) parts.push(`<p style="font-size:10.5pt;color:#2b2f3a;margin:0 0 3pt;"><b>Aandachtspunten:</b> ${esc(c.synthese.aandachtspunten)}</p>`);
        if (hasText(c.synthese.verbeteradvies)) parts.push(p('Verbeteradvies: ' + c.synthese.verbeteradvies));
      }
    } else if (f.key === 'communicatiewerkplaats') {
      (Array.isArray(c.middelen) ? c.middelen : []).forEach((m: any) => {
        parts.push(lab(m.titel || m.typeLabel || 'Middel'));
        if (middelMeta(m)) parts.push(`<p style="font-size:9.5pt;color:#8a90a0;margin:0 0 6pt;">${esc(middelMeta(m))}</p>`);
        alineas(m.tekst).forEach((a) => parts.push(`<p style="font-size:11pt;color:#2b2f3a;margin:0 0 6pt;line-height:1.5;">${esc(a)}</p>`));
        if (Array.isArray(m.openpunten) && m.openpunten.length) {
          parts.push(`<p style="font-size:9.5pt;font-weight:bold;color:#92400e;margin:6pt 0 2pt;">Nog invullen of controleren</p>`);
          parts.push('<ul>' + m.openpunten.map((o: string) => `<li style="font-size:10.5pt;color:#2b2f3a;">${esc(o)}</li>`).join('') + '</ul>');
        }
      });
    }
  });

  parts.push(`<p style="margin:24pt 0 0;padding-top:6pt;border-top:0.5pt solid #D9E2D5;font-size:8pt;color:#8a90a0;">${esc(project.name)} &middot; Opgesteld met OmgevingsCompas &middot; Leene Communicatie</p>`);
  // Eén lettertype voor alles: Word geeft elementen zonder eigen font anders zijn standaardstijl (vaak Times/Calibri).
  const css = `@page { size: 21cm 29.7cm; margin: 2cm; }
    body, p, div, li, ul, ol, td, th, span, b, i, u, a, h1, h2, h3 { font-family: Arial, Helvetica, sans-serif; }
    p, div, li, td { font-size: 11pt; color: #2b2f3a; line-height: 1.45; }
    h1, h2, h3 { color: #1E3A2F; }`;
  const html = `<html xmlns:o="urn:schemas-microsoft-com:office:office" xmlns:w="urn:schemas-microsoft-com:office:word" xmlns="http://www.w3.org/TR/REC-html40"><head><meta charset="utf-8"><title>Dossier ${esc(project.name)}</title><style>${css}</style></head><body style="font-family:Arial,Helvetica,sans-serif;">${parts.join('')}</body></html>`;
  const blob = new Blob(['﻿', html], { type: 'application/msword' });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.setAttribute('download', `Dossier-${safeName(project)}.doc`);
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

// ---------- status-badge ----------
const StatusBadge: React.FC<{ section: any; heeftInhoud: boolean }> = ({ section, heeftInhoud }) => {
  const locked = section?.status === 'locked';
  const bg = locked ? BRAND.groen : heeftInhoud ? BRAND.tint : '#f1f2f5';
  const col = locked ? '#fff' : heeftInhoud ? BRAND.groen : '#8a90a0';
  const label = locked ? 'Gevalideerd' : heeftInhoud ? 'In bewerking' : 'Nog niet gestart';
  const naam = lockerName(section?.lockedBy);
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
      <span style={{ fontSize: 12, fontWeight: 700, borderRadius: 999, padding: '4px 12px', background: bg, color: col }}>{label}</span>
      {locked && section?.lockedAt && (
        <span style={{ fontSize: 12, color: '#8a90a0' }}>op {fmtDate(section.lockedAt)}{naam ? ` · door ${naam}` : ''}</span>
      )}
    </div>
  );
};

export const DossierModule: React.FC<DossierModuleProps> = ({ project }) => {
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const [sections, setSections] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [exportOpen, setExportOpen] = useState(false);

  const klantNaam = (project as any)?.company?.name || '';
  const klantLogo = (project as any)?.company?.logo || '';
  const plaats = (project as any)?.locationData?.city || project.location || '';
  const provincie = (project as any)?.locationData?.province || '';

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dossierAPI.getByProject(project.id);
      setSections(res?.dossier?.sections || {});
    } catch (e) {
      setSections({});
    } finally {
      setLoading(false);
    }
  }, [project.id]);

  useEffect(() => { load(); }, [load]);

  const gevalideerd = sections ? FASE_META.filter((f) => sections[f.key]?.status === 'locked').length : 0;

  const downloadMarkdown = () => {
    const md = buildMarkdown(project, sections || {}, klantNaam, [plaats, provincie].filter(Boolean).join(', '));
    const blob = new Blob([md], { type: 'text/markdown;charset=utf-8' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.setAttribute('download', `Dossier-${project.name.replace(/[^a-z0-9]+/gi, '-')}.md`);
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  };

  if (loading) return <div style={{ padding: 40, color: '#6b7280' }}>Dossier laden…</div>;

  const btn = (primary?: boolean): React.CSSProperties => ({
    display: 'inline-flex', alignItems: 'center', gap: 8, padding: '9px 16px', borderRadius: 9, fontSize: 13.5, fontWeight: 600,
    border: primary ? 'none' : '1px solid #d8dce4', background: primary ? '#1e3a2f' : '#fff', color: primary ? '#fff' : '#374151', cursor: 'pointer',
  });

  return (
    <div style={{ padding: isMobile ? '16px 14px 60px' : '28px 32px 80px', maxWidth: 900, margin: '0 auto' }}>
      {/* Toolbar (niet meegeprint) */}
      <div className="dossier-no-print" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap', marginBottom: 20 }}>
        <button onClick={() => navigate(`/project/${project.id}/dashboard`)} style={{ display: 'inline-flex', alignItems: 'center', gap: 7, padding: '6px 10px 6px 6px', borderRadius: 8, border: 'none', background: 'transparent', color: '#6b7280', fontSize: 13.5, fontWeight: 600, cursor: 'pointer' }}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><line x1="19" y1="12" x2="5" y2="12" /><polyline points="12 19 5 12 12 5" /></svg>
          Terug naar overzicht
        </button>
        <div style={{ position: 'relative' }}>
          <button onClick={() => setExportOpen((o) => !o)} style={btn(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" /><polyline points="7 10 12 15 17 10" /><line x1="12" y1="15" x2="12" y2="3" /></svg>
            Exporteren
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" style={{ marginLeft: 2 }}><polyline points="6 9 12 15 18 9" /></svg>
          </button>
          {exportOpen && (
            <>
              <div onClick={() => setExportOpen(false)} style={{ position: 'fixed', inset: 0, zIndex: 40 }} />
              <div style={{ position: 'absolute', right: 0, top: 'calc(100% + 6px)', zIndex: 41, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.14)', overflow: 'hidden', minWidth: 210 }}>
                {[
                  { key: 'pdf', label: 'Als PDF (.pdf)', sub: 'Direct downloaden', run: () => exportPdf(project, sections || {}, klantNaam, [plaats, provincie].filter(Boolean).join(', ')) },
                  { key: 'word', label: 'Als Word (.doc)', sub: 'Opent in Word', run: () => exportWord(project, sections || {}, klantNaam, [plaats, provincie].filter(Boolean).join(', ')) },
                  { key: 'md', label: 'Als Markdown (.md)', sub: 'Platte tekst', run: downloadMarkdown },
                ].map((opt) => (
                  <button key={opt.key} onClick={() => {
                    setExportOpen(false);
                    Promise.resolve().then(opt.run).catch((e) => { console.error('Export mislukt:', e); window.alert('Exporteren is mislukt. Probeer het opnieuw.'); });
                  }}
                    style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 14px', border: 'none', borderBottom: '1px solid #f2f3f6', background: '#fff', cursor: 'pointer' }}
                    onMouseEnter={(e) => (e.currentTarget.style.background = '#f4f6f1')}
                    onMouseLeave={(e) => (e.currentTarget.style.background = '#fff')}>
                    <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: '#1a1a2e' }}>{opt.label}</span>
                    <span style={{ display: 'block', fontSize: 11.5, color: '#8a90a0', marginTop: 1 }}>{opt.sub}</span>
                  </button>
                ))}
              </div>
            </>
          )}
        </div>
      </div>

      {/* Print-root: alles hierbinnen wordt geprint */}
      <div id="dossier-print-root">
        {/* Cover */}
        <div style={{ ...card, padding: isMobile ? 22 : 32 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 18 }}>
            <img src="/assets/logos/OmgevingsCompas_logo.svg" alt="OmgevingsCompas" style={{ height: 26 }} />
            {klantLogo ? <img src={klantLogo} alt={klantNaam} style={{ height: 40, maxWidth: 160, objectFit: 'contain' }} /> : (klantNaam ? <span style={{ fontSize: 14, fontWeight: 700, color: '#1a1a2e' }}>{klantNaam}</span> : null)}
          </div>
          <div style={{ fontSize: 12.5, fontWeight: 700, color: '#1e3a2f', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Strategisch Fundament</div>
          <h1 style={{ margin: '6px 0 10px', fontSize: isMobile ? 26 : 32, fontWeight: 800, color: '#1a1a2e', lineHeight: 1.2 }}>{project.name}</h1>
          <div style={{ fontSize: 14, color: '#6b7280' }}>
            {[klantNaam, [plaats, provincie].filter(Boolean).join(', ')].filter(Boolean).join(' · ')}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap', marginTop: 18, paddingTop: 16, borderTop: '1px solid #f0f1f4' }}>
            <span style={{ fontSize: 13, color: '#374151' }}><strong style={{ color: BRAND.groen }}>{gevalideerd}/{FASE_META.length}</strong> fasen gevalideerd</span>
            <span style={{ fontSize: 12.5, color: '#9aa1b0' }}>Geëxporteerd op {fmtDateTime(new Date().toISOString())}</span>
          </div>
        </div>

        {/* Fasen */}
        {FASE_META.map((f) => {
          const s = sections?.[f.key] || {};
          const c = s.content || {};
          const heeftInhoud = faseHasContent(f.key, c);
          return (
            <div key={f.key} className="dossier-fase" style={{ ...card, borderTop: `4px solid ${faseKleur(f.nr).base}` }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 14, marginBottom: heeftInhoud ? 20 : 0, flexWrap: 'wrap' }}>
                <span style={{ width: 40, height: 40, borderRadius: 11, background: faseKleur(f.nr).base, color: BRAND.groen, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">{f.icon}</svg>
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12.5, fontWeight: 700, color: '#9aa1b0' }}>Fase {f.nr}</div>
                  <h2 style={{ margin: '1px 0 8px', fontSize: 21, fontWeight: 800, color: '#1a1a2e' }}>{f.titel}</h2>
                  <StatusBadge section={s} heeftInhoud={heeftInhoud} />
                </div>
              </div>

              {!heeftInhoud ? (
                <p style={{ margin: '4px 0 0 54px', fontSize: 14, color: '#9aa1b0' }}>Nog niet ingevuld.</p>
              ) : (
                <div>
                  {f.key === 'projectverkenning' && <RenderFase1 c={c} />}
                  {f.key === 'omgevingsanalyse' && <RenderFase2 c={c} />}
                  {f.key === 'personas' && <RenderFase3 c={c} />}
                  {f.key === 'strategie' && <RenderFase4 c={c} />}
                  {f.key === 'kernboodschap' && <RenderFase5 c={c} />}
                  {f.key === 'communicatiewerkplaats' && <RenderWerkplaats c={c} />}
                </div>
              )}
            </div>
          );
        })}

        <div style={{ textAlign: 'center', fontSize: 12, color: '#b6bcc7', marginTop: 8 }}>
          OmgevingsCompas · Human Driven, AI Powered — {project.name}
        </div>
      </div>
    </div>
  );
};

// ---------- fase-renderers ----------
const RenderFase1: React.FC<{ c: any }> = ({ c }) => (
  <>
    {hasText(c.communicatieopgave) && (
      <div style={{ background: faseKleur(1).soft, border: `1px solid ${faseKleur(1).border}`, borderRadius: 10, padding: '14px 16px', marginBottom: 18 }}>
        <p style={{ ...fieldLabel, color: '#1e3a2f' }}>De communicatieopgave</p>
        {isHtml(c.communicatieopgave)
          ? <div className="rt-content" style={{ ...fieldVal, margin: 0, color: '#1e3a2f', fontWeight: 500, whiteSpace: 'normal' }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(c.communicatieopgave) }} />
          : <p style={{ ...fieldVal, margin: 0, color: '#1e3a2f', fontWeight: 500 }}>{c.communicatieopgave}</p>}
      </div>
    )}
    {F1_FIELDS.map(([k, lab]) => <Field key={k} label={lab} value={c[k]} />)}
    {c.__ai?.verhelderingsvragen?.length > 0 && (
      <div style={{ marginTop: 4 }}>
        <p style={fieldLabel}>Verhelderingsvragen — blinde vlekken</p>
        <ul style={{ margin: '0 0 8px', paddingLeft: 20 }}>
          {c.__ai.verhelderingsvragen.map((v: string, i: number) => <li key={i} style={{ fontSize: 14, color: '#374151', lineHeight: 1.55, marginBottom: 4 }}>{v}</li>)}
        </ul>
      </div>
    )}
  </>
);

const RenderFase2: React.FC<{ c: any }> = ({ c }) => {
  const snap = c.gebiedskaartSnapshot;
  const shl: Stakeholder[] = Array.isArray(c.stakeholdersList) ? c.stakeholdersList : [];
  const nw = c.nieuwsSentiment;
  return (
    <>
      {snap && (
        <div style={{ marginBottom: 20 }}>
          <p style={fieldLabel}>Gebiedsanalyse (gebiedskaart{snap.buurtNaam ? ` — ${snap.buurtNaam}` : ''})</p>
          <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: snap.gebiedsprofielSamenvatting ? 12 : 0 }}>
            <StatTile label="Inwoners" value={num(snap.demografie?.inwoners)} />
            <StatTile label="65-plus" value={snap.demografie?.percentage65Plus != null ? `${snap.demografie.percentage65Plus}%` : '—'} />
            <StatTile label="Gem. inkomen" value={snap.demografie?.gemiddeldInkomen ? `€${num(snap.demografie.gemiddeldInkomen)}` : '—'} />
            <StatTile label="Oppervlakte" value={snap.areaSqmHa != null ? `${num(snap.areaSqmHa)} ha` : '—'} />
          </div>
          {hasText(snap.gebiedsprofielSamenvatting) && <p style={{ ...fieldVal, marginBottom: 0 }}>{snap.gebiedsprofielSamenvatting}</p>}
          {Array.isArray(snap.aandachtspunten) && snap.aandachtspunten.length > 0 && (
            <ul style={{ margin: '8px 0 0', paddingLeft: 20 }}>
              {snap.aandachtspunten.map((a: string, i: number) => <li key={i} style={{ fontSize: 13.5, color: '#4b5563', lineHeight: 1.5 }}>{a}</li>)}
            </ul>
          )}
        </div>
      )}
      {F2_FIELDS.map(([k, lab]) => <Field key={k} label={lab} value={c[k]} />)}
      {shl.length > 0 && (
        <div style={{ marginBottom: 8 }}>
          <p style={fieldLabel}>Stakeholders ({shl.length})</p>
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13.5, minWidth: 560 }}>
              <thead>
                <tr style={{ textAlign: 'left', color: '#8a90a0' }}>
                  {['Stakeholder', 'Groep', 'Invloed', 'Belang', 'Houding', 'Aanpak'].map((h) => <th key={h} style={{ padding: '6px 10px', fontWeight: 600, borderBottom: '1px solid #eceef2' }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {shl.map((s) => {
                  const ap = aanpakVoor(s.invloed, s.belang);
                  return (
                    <tr key={s.id} style={{ borderBottom: '1px solid #f2f3f6' }}>
                      <td style={{ padding: '8px 10px', fontWeight: 600, color: '#1a1a2e' }}>{s.naam || '—'}</td>
                      <td style={{ padding: '8px 10px', color: '#4b5563' }}>{s.groep || '—'}</td>
                      <td style={{ padding: '8px 10px', color: '#4b5563' }}>{niveauDot(s.invloed).label}</td>
                      <td style={{ padding: '8px 10px', color: '#4b5563' }}>{niveauDot(s.belang).label}</td>
                      <td style={{ padding: '8px 10px', color: houding(s.houding).c, fontWeight: 600 }}>{houding(s.houding).l}</td>
                      <td style={{ padding: '8px 10px' }}><span style={{ fontSize: 12, fontWeight: 600, color: ap.color, background: ap.bg, borderRadius: 999, padding: '2px 9px' }}>{ap.label}</span></td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
      {nw && (
        <div style={{ marginTop: 16, paddingTop: 16, borderTop: '1px solid #f0f1f4' }}>
          <p style={fieldLabel}>Nieuws & sentiment</p>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: hasText(nw.samenvatting) ? 8 : 0 }}>
            <span style={{ fontSize: 12.5, fontWeight: 700, color: '#fff', background: sentColor(nw.overall), borderRadius: 999, padding: '3px 11px', textTransform: 'capitalize' }}>{nw.overall}</span>
            <span style={{ fontSize: 12.5, color: '#6b7280' }}>{Array.isArray(nw.items) ? nw.items.length : 0} bericht(en) · score {nw.score > 0 ? `+${nw.score}` : nw.score}</span>
          </div>
          {hasText(nw.samenvatting) && <p style={{ ...fieldVal, marginBottom: 0 }}>{nw.samenvatting}</p>}
        </div>
      )}
    </>
  );
};

const chip = (bg: string, col: string): React.CSSProperties => ({ fontSize: 11.5, fontWeight: 600, color: col, background: bg, borderRadius: 999, padding: '3px 10px' });

const RenderFase3: React.FC<{ c: any }> = ({ c }) => {
  const ps: any[] = dossierPersonas(c);
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {ps.map((p, i) => (
        <div key={p.id || i} style={{ border: '1px solid #eceef2', borderRadius: 12, padding: 16 }}>
          <div style={{ display: 'flex', gap: 14, alignItems: 'flex-start', marginBottom: 12 }}>
            {p.afbeelding ? <img src={p.afbeelding} alt={p.naam} style={{ width: 56, height: 56, borderRadius: '50%', objectFit: 'cover', flexShrink: 0 }} /> : <span style={{ width: 56, height: 56, borderRadius: '50%', background: '#e8eee6', flexShrink: 0 }} />}
            <div style={{ flex: 1, minWidth: 0 }}>
              <h3 style={{ margin: 0, fontSize: 17, fontWeight: 800, color: '#1a1a2e' }}>{p.naam || 'Naamloze persona'}</h3>
              {hasText(p.groep) && <div style={{ fontSize: 13, color: '#6b7280', marginTop: 1 }}>{p.groep}</div>}
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                {hasText(p.leeftijd) && <span style={chip('#f1f2f5', '#4b5563')}>{p.leeftijd} jaar</span>}
                {hasText(p.geslacht) && <span style={chip('#f1f2f5', '#4b5563')}>{p.geslacht}</span>}
                {hasText(p.participatieniveau) && <span style={chip('#e8eee6', '#1e3a2f')}>{p.participatieniveau}</span>}
              </div>
              {hasText(p.gebaseerdOp) && <div style={{ fontSize: 12.5, color: '#8a90a0', marginTop: 8 }}>Gebaseerd op stakeholders: <strong style={{ color: '#4b5563' }}>{p.gebaseerdOp}</strong></div>}
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '0 20px' }}>
            {PERSONA_FIELDS.map(([k, lab]) => <Field key={k} label={lab} value={p[k]} />)}
            {(hasText(p.huidigGedrag) || hasText(p.gewenstGedrag)) && (
              <div><p style={fieldLabel}>Gedrag</p><p style={fieldVal}>{p.huidigGedrag || '—'} → {p.gewenstGedrag || '—'}</p></div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

const RenderFase4: React.FC<{ c: any }> = ({ c }) => {
  const dg: any[] = dossierDoelgroepen(c);
  const sp: any[] = Array.isArray(c.communicatiesporen) ? c.communicatiesporen : [];
  const fs: any[] = Array.isArray(c.fasering) ? c.fasering : [];
  return (
    <>
      {c.modelKeuze && <Field label="Gekozen communicatiemodel" value={MODEL_LABEL[c.modelKeuze] || c.modelKeuze} />}
      {F4_FIELDS.map(([k, lab]) => <Field key={k} label={lab} value={c[k]} />)}
      {sp.length > 0 && (
        <div style={{ marginTop: 4, marginBottom: 16 }}>
          <p style={fieldLabel}>Communicatiesporen</p>
          <div style={{ display: 'grid', gap: 12 }}>
            {sp.map((s, i) => (
              <div key={i} style={{ border: '1px solid #eceef2', borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>{s.naam || `Spoor ${i + 1}`}</div>
                <div style={{ display: 'grid', gap: '0 20px', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                  <Field label="Doel" value={s.doel} />
                  <Field label="Betrokkenen" value={s.betrokkenen} />
                  <Field label="Gewenste beweging/effect" value={s.gewensteBeweging} />
                  <Field label="Kern van de aanpak" value={s.kernAanpak} />
                  <Field label="Risico’s & randvoorwaarden" value={s.risicos} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {fs.length > 0 && (
        <div style={{ marginTop: 4, marginBottom: 16 }}>
          <p style={fieldLabel}>Fasering & mijlpalen</p>
          <div style={{ display: 'grid', gap: 12 }}>
            {fs.map((s, i) => (
              <div key={i} style={{ border: '1px solid #eceef2', borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>{s.fase || `Fase ${i + 1}`}</div>
                <div style={{ display: 'grid', gap: '0 20px', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                  <Field label="Communicatieopgave" value={s.opgave} />
                  <Field label="Belangrijke momenten & mijlpalen" value={s.mijlpalen} />
                  <Field label="Aanpak" value={s.aanpak} />
                  <Field label="Risico’s & overgangsvoorwaarden" value={s.risicos} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {dg.length > 0 && (
        <div style={{ marginTop: 4 }}>
          <p style={fieldLabel}>Strategie per doelgroep</p>
          <div style={{ display: 'grid', gap: 12 }}>
            {dg.map((d, i) => (
              <div key={d.personaId || i} style={{ border: '1px solid #eceef2', borderRadius: 10, padding: 14 }}>
                <div style={{ fontSize: 15, fontWeight: 700, color: '#1a1a2e', marginBottom: 8 }}>{d.persona || 'Doelgroep'}</div>
                <div style={{ display: 'grid', gap: '0 20px', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
                  <Field label="Doel (kennis / houding / gedrag)" value={d.doel} />
                  <Field label="Benadering" value={d.benadering} />
                  <Field label="Participatieniveau" value={d.participatieniveau} />
                  <Field label="Middelen" value={d.middelen} />
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </>
  );
};

/** Fase 6 — de middelen die in de Communicatiewerkplaats zijn opgezet. */
const RenderWerkplaats: React.FC<{ c: any }> = ({ c }) => {
  const middelen: any[] = Array.isArray(c.middelen) ? c.middelen : [];
  return (
    <div style={{ display: 'grid', gap: 16 }}>
      {middelen.map((m, i) => (
        <div key={m.id || i} style={{ border: '1px solid #ececf1', borderRadius: 10, padding: '14px 16px' }}>
          <p style={{ margin: 0, fontSize: 15.5, fontWeight: 700, color: '#1a1a2e' }}>{m.titel || m.typeLabel || 'Middel'}</p>
          {middelMeta(m) && <p style={{ margin: '2px 0 10px', fontSize: 12.5, color: '#8a90a0' }}>{middelMeta(m)}</p>}
          {alineas(m.tekst).map((a, j) => (
            <p key={j} style={{ margin: '0 0 8px', fontSize: 14, color: '#374151', lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>{a}</p>
          ))}
          {Array.isArray(m.openpunten) && m.openpunten.length > 0 && (
            <div style={{ marginTop: 6, padding: '10px 12px', borderRadius: 8, background: '#fef8ec', border: '1px solid #f3e3c3' }}>
              <p style={{ margin: '0 0 4px', fontSize: 12, fontWeight: 700, color: '#92400e' }}>Nog invullen of controleren</p>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {m.openpunten.map((o: string, j: number) => <li key={j} style={{ fontSize: 12.5, color: '#7c4a11', lineHeight: 1.5 }}>{o}</li>)}
              </ul>
            </div>
          )}
        </div>
      ))}
    </div>
  );
};

const RenderFase5: React.FC<{ c: any }> = ({ c }) => {
  const db: string[] = Array.isArray(c.deelboodschappen) ? c.deelboodschappen.filter(hasText) : [];
  const sy = c.synthese;
  return (
    <>
      {hasText(c.kernboodschap) && (
        <div style={{ background: faseKleur(5).soft, border: `1px solid ${faseKleur(5).border}`, borderRadius: 10, padding: '16px 18px', marginBottom: 18 }}>
          <p style={{ ...fieldLabel, color: '#1e3a2f' }}>Centrale kernboodschap</p>
          {isHtml(c.kernboodschap)
            ? <div className="rt-content" style={{ margin: 0, fontSize: 17, fontWeight: 600, color: '#1e3a2f', lineHeight: 1.5 }} dangerouslySetInnerHTML={{ __html: sanitizeHtml(c.kernboodschap) }} />
            : <p style={{ margin: 0, fontSize: 17, fontWeight: 600, color: '#1e3a2f', lineHeight: 1.5 }}>{c.kernboodschap}</p>}
        </div>
      )}
      {db.length > 0 && (
        <div style={{ marginBottom: sy ? 18 : 0 }}>
          <p style={fieldLabel}>Deelboodschappen</p>
          <ul style={{ margin: 0, paddingLeft: 20 }}>
            {db.map((d, i) => <li key={i} style={{ fontSize: 14, color: '#374151', lineHeight: 1.55, marginBottom: 5 }}>{d}</li>)}
          </ul>
        </div>
      )}
      {sy && (
        <div style={{ paddingTop: 14, borderTop: '1px solid #f0f1f4' }}>
          <p style={fieldLabel}>Pretest — totaaladvies</p>
          {hasText(sy.algemeneReactie) && <p style={fieldVal}>{sy.algemeneReactie}</p>}
          {hasText(sy.sterk) && <p style={{ ...fieldVal, marginBottom: 8 }}><strong style={{ color: '#1f7a44' }}>Sterk:</strong> {sy.sterk}</p>}
          {hasText(sy.aandachtspunten) && <p style={{ ...fieldVal, marginBottom: 8 }}><strong style={{ color: '#9a6a00' }}>Aandachtspunten:</strong> {sy.aandachtspunten}</p>}
          {hasText(sy.verbeteradvies) && (
            <div style={{ background: '#f7f8fa', borderRadius: 10, padding: '12px 14px', marginTop: 4 }}>
              <p style={fieldLabel}>Verbeteradvies</p>
              <p style={{ ...fieldVal, margin: 0 }}>{sy.verbeteradvies}</p>
            </div>
          )}
        </div>
      )}
    </>
  );
};
